import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate, useParams } from 'react-router'
import { useState } from 'react'
import type { AccessLevel, Role } from '@famora/core'
import { ApiFehler, hole, schicke } from '../lib/api'
import { geburtstagFormatiert } from '../lib/datum'
import { mitgliedsFarbe, rollenName, stufenName, zahlAlsSatz } from '../session/logic'
import { useDarf, useSitzung } from '../session/SessionProvider'
import { Avatar, Button, Card, Empty, SegmentedButtons, Switch, useConfirm, useToast } from '../ui'
import { MitgliedBearbeitenSheet } from './MitgliedBearbeitenSheet'
import { MitgliedLoeschenSheet } from './MitgliedLoeschenSheet'

const STUFEN: AccessLevel[] = ['none', 'read', 'write', 'manage']

interface ZugriffAntwort {
  memberId: string
  role: string
  canManagePermissions: boolean
  overrides: { capability: string; effect: 'allow' | 'deny' }[]
  modules: { key: string; label: string; level: AccessLevel; quelle: 'preset' | 'gesetzt' }[]
}

interface Mitglied {
  id: string
  displayName: string
  role: Role
  color: string
  birthDate?: string | null
  status: 'active' | 'invited' | 'disabled'
  hasLogin: boolean
  loginMethod: 'google' | 'code' | null
}

interface Zugangsdaten {
  code: string
  pin: string
}

/**
 * Die Bereichsfreischaltung — der Bildschirm, um den es dem Produkt geht.
 *
 * Vier schlichte Schritte je Bereich, die Quelle der Stufe benannt, und die
 * ehrliche Zahl aus der Vorschau. Kein Fachwort, keine Schalter.
 */
export function MitgliedRechte() {
  const { id = '' } = useParams()
  const qc = useQueryClient()
  const navigate = useNavigate()
  const [fehler, setFehler] = useState<string | null>(null)
  const [bearbeitenOffen, setBearbeitenOffen] = useState(false)
  const [loeschenOffen, setLoeschenOffen] = useState(false)
  const [vorschauFuer, setVorschauFuer] = useState<string | null>(null)
  const [zugangsdaten, setZugangsdaten] = useState<Zugangsdaten | null>(null)
  const [kopiert, setKopiert] = useState<string | null>(null)
  const darfStundenplan = useDarf('timetable.view')
  const darfMitgliederVerwalten = useDarf('member.manage')
  const { abfrage: sitzungAbfrage } = useSitzung()
  const zeigeToast = useToast()
  const bestaetige = useConfirm()

  const meinMember =
    sitzungAbfrage.data && !sitzungAbfrage.data.setupRequired ? sitzungAbfrage.data.member : null
  const meinMemberId = meinMember?.id ?? null
  const istSelbst = Boolean(meinMemberId && meinMemberId === id)
  const istOwner = Boolean(
    sitzungAbfrage.data &&
    !sitzungAbfrage.data.setupRequired &&
    sitzungAbfrage.data.member.role === 'owner',
  )

  const mitglieder = useQuery({
    queryKey: ['members'],
    queryFn: () => hole<Mitglied[]>('/api/members'),
  })
  const zugriff = useQuery({
    queryKey: ['access', id],
    queryFn: () => hole<ZugriffAntwort>(`/api/members/${id}/access`),
    enabled: id !== '',
  })

  const person = mitglieder.data?.find((m) => m.id === id)

  const darfBearbeiten =
    darfMitgliederVerwalten && (!person || istOwner || person.role !== 'owner' || istSelbst)

  const overrideSetzen = useMutation({
    mutationFn: (v: { capability: string; effect: 'allow' | 'deny' | 'default' }) =>
      schicke('PUT', `/api/members/${id}/access/override`, v),
    onMutate: () => {
      setFehler(null)
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['access', id] })
      zeigeToast('Berechtigung aktualisiert', 'erfolg')
    },
    onError: (f: unknown) => {
      const meldung = f instanceof ApiFehler ? f.message : 'Speichern fehlgeschlagen.'
      setFehler(meldung)
      zeigeToast(meldung, 'error')
    },
  })

  const setzen = useMutation({
    mutationFn: (v: { moduleKey: string; level: AccessLevel }) =>
      schicke('PUT', `/api/members/${id}/access`, v),
    onMutate: () => {
      setFehler(null)
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['access', id] })
      zeigeToast('Berechtigungsstufe aktualisiert', 'erfolg')
    },
    onError: (f: unknown) => {
      const meldung = f instanceof ApiFehler ? f.message : 'Speichern fehlgeschlagen.'
      setFehler(meldung)
      zeigeToast(meldung, 'error')
    },
  })

  const vorschau = useQuery({
    queryKey: ['access-preview', id, vorschauFuer],
    queryFn: () =>
      hole<{ sichtbar: number; zaehlbar: boolean }>(
        `/api/members/${id}/access/preview?moduleKey=${vorschauFuer ?? ''}&level=read`,
      ),
    enabled: vorschauFuer !== null,
  })

  const alsAnsehen = useMutation({
    mutationFn: () => schicke('POST', '/api/preview', { memberId: id }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['me'] })
      void navigate('/')
    },
  })

  const zugangErzeugen = useMutation({
    mutationFn: () => schicke<Zugangsdaten>('POST', `/api/members/${id}/code`),
    onMutate: () => {
      setFehler(null)
      setKopiert(null)
    },
    onSuccess: async (daten) => {
      setZugangsdaten(daten)
      await qc.invalidateQueries({ queryKey: ['members'] })
      zeigeToast('Neuer persönlicher Zugang erstellt', 'erfolg')
    },
    onError: (f: unknown) => {
      const meldung =
        f instanceof ApiFehler ? f.message : 'Der Zugang konnte nicht erstellt werden.'
      setFehler(meldung)
      zeigeToast(meldung, 'error')
    },
  })

  const einladungsLink = zugangsdaten
    ? `${window.location.origin}/?zugang=invite&code=${encodeURIComponent(zugangsdaten.code)}`
    : ''
  const googleEinladungsLink = `${einladungsLink}&google=1`

  async function kopieren(text: string, was: string) {
    try {
      await navigator.clipboard.writeText(text)
      setKopiert(was)
      zeigeToast(`${was} in die Zwischenablage kopiert`, 'erfolg')
    } catch {
      const meldung = 'Kopieren war nicht möglich. Bitte markiere die Angabe von Hand.'
      setFehler(meldung)
      zeigeToast(meldung, 'error')
    }
  }

  const farbe = person ? mitgliedsFarbe(person.color, person.id) : undefined

  return (
    <>
      <p className="unterzeile">
        <Link to="/familie">Zurück zur Familie</Link>
      </p>

      <div className="seitenkopf seitenkopf--person">
        {farbe ? <Avatar name={person?.displayName ?? '?'} farbe={farbe} gross /> : null}
        <div className="seitenkopf__info">
          <h1>{person?.displayName ?? 'Person'}</h1>
          <p>
            {person ? rollenName(person.role) : ''}
            {person?.birthDate ? ` · Geboren: ${geburtstagFormatiert(person.birthDate)}` : ''}
            {person && !person.hasLogin ? ' · noch kein Zugang' : ''}
            {person?.status === 'disabled' ? ' · Deaktiviert' : ''}
          </p>
        </div>
        {darfBearbeiten && person ? (
          <Button
            variant="tonal"
            onClick={() => {
              setBearbeitenOffen(true)
            }}
          >
            Profil bearbeiten
          </Button>
        ) : null}
      </div>

      {darfStundenplan && person && person.role !== 'guest' ? (
        <p className="unterzeile">
          <Link to={`/stundenplan?mitglied=${person.id}`}>
            Stundenplan von {person.displayName}
          </Link>
        </p>
      ) : null}

      {fehler ? (
        <p className="fehler" role="alert">
          {fehler}
        </p>
      ) : null}

      {zugriff.data && zugriff.data.modules.length === 0 ? (
        <Empty
          titel="Noch keine Bereiche"
          text="Sobald Famora Bereiche wie Aufgaben oder Einkauf mitbringt, kannst du sie hier einzeln freischalten."
        />
      ) : null}

      {zugriff.data && zugriff.data.modules.length > 0 ? (
        <Card titel="Bereiche">
          <p className="unterzeile">
            {istSelbst
              ? 'Die eigenen Modulstufen kann man bei sich selbst nicht ändern. Sie ergeben sich aus deiner Rolle.'
              : 'Was diese Person in welchem Bereich darf. Was aus der Rolle kommt, ist gekennzeichnet — alles andere hast du selbst gesetzt.'}
          </p>
          <div className="stack-6">
            {zugriff.data.modules.map((m) => (
              <div className="stack-2" key={m.key}>
                <h3 className="abschnitt-titel">{m.label}</h3>
                <SegmentedButtons<AccessLevel>
                  label={`Stufe für ${m.label}`}
                  wert={m.level}
                  optionen={STUFEN.map((s) => ({ wert: s, label: stufenName(s) }))}
                  onWert={(s) => {
                    if (istSelbst) {
                      zeigeToast(
                        'Die eigenen Modulstufen kann man bei sich selbst nicht ändern',
                        'error',
                      )
                      return
                    }
                    setzen.mutate({ moduleKey: m.key, level: s })
                  }}
                />
                <p className="unterzeile">
                  {m.quelle === 'preset' ? 'aus der Rolle' : 'von dir gesetzt'}
                </p>
                {vorschauFuer === m.key ? (
                  <p className="folge">
                    {vorschau.isPending
                      ? 'Wird gezählt …'
                      : zahlAlsSatz(
                          person?.displayName ?? 'Diese Person',
                          vorschau.data?.zaehlbar ? vorschau.data.sichtbar : null,
                        )}
                  </p>
                ) : (
                  <div className="reihe">
                    <Button
                      variant="text"
                      onClick={() => {
                        setVorschauFuer(m.key)
                      }}
                    >
                      Was sieht sie dadurch?
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </Card>
      ) : null}

      {person && zugriff.data && !istSelbst && zugriff.data.canManagePermissions ? (
        <Card titel="Erweiterte Berechtigungen">
          <p className="unterzeile">
            Zusätzliche Freigaben und Einstellungen für dieses Familienmitglied.
          </p>
          <div className="stack-4">
            {person.role === 'adult' ? (
              <div className="stack-2">
                <Switch
                  label="Berechtigungen verwalten (Co-Elternteil)"
                  checked={zugriff.data.overrides.some(
                    (o) => o.capability === 'permission.grant' && o.effect === 'allow',
                  )}
                  disabled={overrideSetzen.isPending}
                  onChange={(an) => {
                    overrideSetzen.mutate({
                      capability: 'permission.grant',
                      effect: an ? 'allow' : 'default',
                    })
                  }}
                />
                <p className="unterzeile">
                  Erlaubt dieser erwachsenen Person, Zugriffsstufen und Berechtigungen anderer
                  Familienmitglieder anzupassen.
                </p>
              </div>
            ) : null}

            {person.role === 'child' || person.role === 'teen' ? (
              <div className="stack-2">
                <Switch
                  label="Aufgaben eigenständig abhaken"
                  checked={
                    zugriff.data.modules.find((m) => m.key === 'tasks')?.level === 'write' ||
                    zugriff.data.modules.find((m) => m.key === 'tasks')?.level === 'manage' ||
                    zugriff.data.overrides.some(
                      (o) => o.capability === 'tasks.complete' && o.effect === 'allow',
                    )
                  }
                  disabled={
                    overrideSetzen.isPending ||
                    zugriff.data.modules.find((m) => m.key === 'tasks')?.level === 'write' ||
                    zugriff.data.modules.find((m) => m.key === 'tasks')?.level === 'manage'
                  }
                  onChange={(an) => {
                    overrideSetzen.mutate({
                      capability: 'tasks.complete',
                      effect: an ? 'allow' : 'default',
                    })
                  }}
                />
                <p className="unterzeile">
                  {zugriff.data.modules.find((m) => m.key === 'tasks')?.level === 'write' ||
                  zugriff.data.modules.find((m) => m.key === 'tasks')?.level === 'manage'
                    ? 'Aufgaben-Stufe erlaubt bereits die volle Mitarbeit inklusive Abhaken.'
                    : zugriff.data.overrides.some(
                          (o) => o.capability === 'tasks.complete' && o.effect === 'allow',
                        )
                      ? 'Aktiv: Kann Aufgaben als erledigt abhaken, ohne fremde Aufgaben löschen oder umschreiben zu können.'
                      : 'Aus: Kann Aufgaben im Haushalt nur mitlesen.'}
                </p>
              </div>
            ) : null}

            <div className="stack-1">
              <h4 className="abschnitt-titel">Privatsphäre & Eltern-Einsicht</h4>
              <p className="unterzeile">
                {person.role === 'child'
                  ? 'Transparenz (§4.1): Für Kinder sind als privat markierte Einträge für Erziehungsberechtigte sichtbar. Dem Kind wird in der App transparent ein Schloss-Symbol angezeigt.'
                  : person.role === 'teen'
                    ? 'Transparenz (§4.1): Jugendliche verwalten ihre privaten Einträge eigenständig. Diese sind für Erziehungsberechtigte nicht einsehbar.'
                    : 'Transparenz (§4.1): Erwachsene verwalten ihre privaten Einträge vollständig selbstständig.'}
              </p>
            </div>
          </div>
        </Card>
      ) : null}

      {person && person.loginMethod !== 'google' ? (
        <Card titel="Zugang" ton="high">
          <p className="unterzeile">
            {person.loginMethod === 'code'
              ? 'Ein neuer Code macht den bisherigen sofort ungültig.'
              : `Erstelle einen persönlichen Zugang für ${person.displayName}. Code und PIN werden nur dieses eine Mal gemeinsam angezeigt.`}
          </p>
          <div className="reihe">
            <Button
              laeuft={zugangErzeugen.isPending}
              onClick={() => {
                if (person.loginMethod !== 'code') {
                  zugangErzeugen.mutate()
                  return
                }
                void bestaetige({
                  titel: 'Zugang ersetzen?',
                  text: 'Den bisherigen Zugang wirklich ersetzen?',
                  bestaetigen: 'Ersetzen',
                  gefahr: true,
                }).then((bestaetigt) => {
                  if (bestaetigt) zugangErzeugen.mutate()
                })
              }}
            >
              {person.loginMethod === 'code' ? 'Neuen Code erstellen' : 'Zugang erstellen'}
            </Button>
          </div>

          {zugangsdaten ? (
            <div className="zugangsdaten" aria-live="polite">
              <p className="zugang-hinweis">
                Jetzt weitergeben oder sicher notieren. Nach Verlassen dieser Seite wird die PIN
                nicht erneut angezeigt.
              </p>
              <dl>
                <div>
                  <dt>Code</dt>
                  <dd>{zugangsdaten.code}</dd>
                </div>
                <div>
                  <dt>PIN</dt>
                  <dd>{zugangsdaten.pin}</dd>
                </div>
              </dl>
              <div className="reihe">
                <Button
                  variant="outlined"
                  onClick={() => {
                    void kopieren(einladungsLink, 'link')
                  }}
                >
                  Einladungslink kopieren
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => {
                    void kopieren(zugangsdaten.pin, 'pin')
                  }}
                >
                  PIN kopieren
                </Button>
                {['owner', 'adult'].includes(person.role) ? (
                  <Button
                    variant="outlined"
                    onClick={() => {
                      void kopieren(googleEinladungsLink, 'google')
                    }}
                  >
                    Google-Einladung kopieren
                  </Button>
                ) : null}
              </div>
              {kopiert ? (
                <p className="unterzeile" role="status">
                  {kopiert === 'link'
                    ? 'Einladungslink kopiert.'
                    : kopiert === 'google'
                      ? 'Google-Einladung kopiert.'
                      : 'PIN kopiert.'}
                </p>
              ) : null}
              {['owner', 'adult'].includes(person.role) ? (
                <p className="unterzeile">
                  Mit der Google-Einladung führt die PIN nach der Bestätigung direkt zu Google. Der
                  Code wird nach erfolgreicher Verknüpfung automatisch entfernt. Eine zuvor
                  abgebrochene Registrierung und weitere konfliktfreie Haushalte werden sicher
                  übernommen.
                </p>
              ) : null}
            </div>
          ) : null}
        </Card>
      ) : person?.loginMethod === 'google' ? (
        <Card titel="Zugang" ton="high">
          <p className="unterzeile">Diese Person meldet sich sicher über Google an.</p>
        </Card>
      ) : null}

      <Card titel="Nachsehen">
        <p className="unterzeile">
          Famora zeigt dir die App genau so, wie {person?.displayName ?? 'diese Person'} sie sieht.
          Änderungen sind dabei gesperrt.
        </p>
        <div className="reihe">
          <Button
            variant="outlined"
            laeuft={alsAnsehen.isPending}
            onClick={() => {
              alsAnsehen.mutate()
            }}
          >
            Als {person?.displayName ?? 'diese Person'} ansehen
          </Button>
        </div>
      </Card>

      {darfBearbeiten && person && !istSelbst && person.status === 'disabled' ? (
        <Card titel="Person endgültig löschen">
          <p className="unterzeile">
            Die Person ist deaktiviert. Wenn sie sicher nicht zurückkehrt, kannst du ihr Profil und
            die damit verbundenen persönlichen Daten dauerhaft entfernen.
          </p>
          <div className="reihe">
            <Button
              variant="outlined"
              className="btn--gefahr"
              onClick={() => setLoeschenOffen(true)}
            >
              Endgültig löschen
            </Button>
          </div>
        </Card>
      ) : null}

      <MitgliedBearbeitenSheet
        offen={bearbeitenOffen}
        onSchliessen={() => setBearbeitenOffen(false)}
        mitglied={person ?? null}
        istSelbst={istSelbst}
        istOwner={istOwner}
      />
      <MitgliedLoeschenSheet
        offen={loeschenOffen}
        onSchliessen={() => setLoeschenOffen(false)}
        onGeloescht={() => {
          setLoeschenOffen(false)
          void navigate('/familie')
        }}
        mitglied={person ?? null}
      />
    </>
  )
}
